import { Buffer } from 'buffer';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import Avatar from 'react-avatar';

import { getAttendee, getQrCode, getTicket } from '@apiCalls';
import { LoadingAnimation } from '@assets';
import { DATE_FORMAT_SHORT } from '@constants';
import { useAppContext } from '@contexts';
import tracker from '@utils/tracking';

import { AddToWallet } from './AddToWallet';

type ITicketPurchasedProperties = {
  purchasedAttendeeTicket: IAttendeeTicket;
};

export const TicketPurchased = ({
  purchasedAttendeeTicket,
}: ITicketPurchasedProperties) => {
  const { event, eventSeries } = useAppContext();
  const [ticket, setTicket] = useState<ITicket>();
  const { data: attendee } = useQuery({
    queryKey: ['attendee', purchasedAttendeeTicket.AttendeeID],
    queryFn: () =>
      purchasedAttendeeTicket.AttendeeID
        ? getAttendee(purchasedAttendeeTicket.AttendeeID)
        : undefined,
  });
  const { data: ticketData } = useQuery({
    queryKey: ['ticket', purchasedAttendeeTicket.TicketID],
    queryFn: () =>
      purchasedAttendeeTicket.TicketID
        ? getTicket(purchasedAttendeeTicket.TicketID)
        : undefined,
  });

  useEffect(() => {
    tracker.track({
      event: {
        event: 'Checkout Completed',
        category: 'Complete',
        value: eventSeries?.ID ?? event.ID,
      },
      platforms: ['ga'],
    });
    setTicket(ticketData);
  }, [ticketData]);

  return (
    <div className='ticketPurchased'>
      {ticket ? (
        <>
          <div className='ticketEventInformation'>
            <h1>{event.Name || eventSeries?.Name}</h1>
            <h3>
              {event.VenueName ? (
                <>{event.VenueName}</>
              ) : eventSeries?.VenueName ? (
                <>{eventSeries.VenueName}</>
              ) : (
                <></>
              )}
              {event.Date ? (
                <>&nbsp;•&nbsp; {dayjs(event.Date).format(DATE_FORMAT_SHORT)}</>
              ) : eventSeries?.StartDate && eventSeries.EndDate ? (
                <>
                  {dayjs(eventSeries.StartDate).format(DATE_FORMAT_SHORT)}
                  &nbsp;•&nbsp;
                  {dayjs(eventSeries.EndDate).format(DATE_FORMAT_SHORT)}
                </>
              ) : (
                <></>
              )}
            </h3>
          </div>
          <div className='ticketQRCodeContainer'>
            <QRCode
              attendeeTicketKey={purchasedAttendeeTicket.AttendeeTicketKey}
              secondaryColor={
                event.ColourSecondary || eventSeries?.ColourSecondary || ''
              }
              backgroundColor={
                event.ColourBackground || eventSeries?.ColourBackground || ''
              }
            />
          </div>
          <div className='attendeeDetails'>
            <div className='attendeeName'>
              <h2>
                {attendee?.FirstName} {attendee?.LastName}
              </h2>
              <h3>{ticket.Name}</h3>
            </div>
            <div>
              <div className='attendeeAvatar'>
                {attendee?.Photo ? (
                  <img src={attendee?.Photo} alt='Avatar' draggable='false' />
                ) : (
                  <Avatar
                    name='1'
                    color={
                      event?.ColourSecondary ||
                      eventSeries?.ColourSecondary ||
                      ''
                    }
                    fgColor={
                      event?.ColourBackground ||
                      eventSeries?.ColourBackground ||
                      ''
                    }
                    size='50'
                  />
                )}
              </div>
            </div>
          </div>
          <AddToWallet
            attendeeTicketKey={purchasedAttendeeTicket.AttendeeTicketKey}
            eventId={eventSeries?.ID ?? event.ID}
          />
        </>
      ) : (
        <LoadingAnimation fill='rgb(var(--primary-colour))' height={80} />
      )}
    </div>
  );
};

const QRCode = ({
  attendeeTicketKey,
  backgroundColor,
  secondaryColor,
}: {
  attendeeTicketKey?: string;
  backgroundColor: string;
  secondaryColor: string;
}) => {
  const [qrString, setQrString] = useState<string>();
  const { data: qrCodeData, isLoading: isLoadingQrCode } = useQuery({
    queryKey: ['qrCode', attendeeTicketKey],
    queryFn: () =>
      attendeeTicketKey
        ? getQrCode(
            attendeeTicketKey,
            backgroundColor.slice(1),
            secondaryColor.slice(1)
          )
        : undefined,
  });

  useEffect(() => {
    if (qrCodeData) {
      setQrString(Buffer.from(qrCodeData).toString('base64'));
    }
  }, [qrCodeData]);

  return (
    <>
      {isLoadingQrCode || !qrString ? (
        <LoadingAnimation fill='rgb(var(--primary-colour))' height={80} />
      ) : (
        <img
          src={`data:image/svg+xml;base64,${qrString}`}
          draggable='false'
          alt='qr-svg'
        />
      )}
    </>
  );
};

export default TicketPurchased;
